import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";

const Row = ({ label, value, id }) => (
  <div className="flex justify-between border-b border-slate-100 py-1.5 text-sm" data-testid={`nc-${id}`}><span className="text-slate-600">{label}</span><b>{value ?? "—"}</b></div>
);

export function NewCatalogueStep() {
  const [plan, setPlan] = useState(null);
  const [gaps, setGaps] = useState(null);
  const [busy, setBusy] = useState("");
  const load = useCallback(() => {
    api.get("/new-catalogue/plan").then((r) => setPlan(r.data)).catch((e) => toast.error(e.response?.data?.detail || "Could not load plan"));
    api.get("/new-catalogue/data-gaps").then((r) => setGaps(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!gaps?.image_copy?.running) return undefined;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [gaps, load]);
  const run = async (path, msg) => {
    if (!window.confirm(msg)) return;
    setBusy(path);
    try {
      if (path === "import") {
        await api.post("/catalogue-migration/snapshot/refresh", {}, { timeout: 280000 });
        const r = await api.post("/new-catalogue/import", { confirm: true, singles_only: true }, { timeout: 120000 });
        toast.success(`${r.data.singles} individual products imported/updated (bundles untouched)`);
      } else {
        await api.post(`/new-catalogue/${path}`, { confirm: true }, { timeout: 120000 });
        toast.success("Done");
      }
      load();
    }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(""); }
  };
  if (!plan) return <Loader2 className="w-5 h-5 animate-spin" />;
  const ic = gaps?.image_copy;
  return (
    <div className="grid gap-4 lg:grid-cols-2" data-testid="nc-step">
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <h3 className="font-semibold mb-2">New accessories catalogue from /shop/</h3>
        <Row id="singles" label="Individual products in TedOps (stock starts at 0)" value={plan.new_singles_in_tedops} />
        <Row id="bundles" label="Bundles in TedOps (linked to their individual products)" value={plan.new_bundles_in_tedops} />
        <Row id="blocked" label="Bundles not for sale until corrected on the website" value={plan.bundles_blocked} />
        <ul className="mt-1 text-xs text-slate-600" data-testid="nc-blocked-list">
          {(plan.blocked_list || []).map((b) => <li key={b.legacy_id}><Link className="text-indigo-700 hover:underline" to={`/products/${b.legacy_id}`}>#{b.legacy_id} {b.title}</Link> — {b.reasons.join("; ")}</li>)}
        </ul>
        <p className="mt-2 text-xs text-slate-500">Re-running updates the same records from the latest website read; it never duplicates.</p>
        <Button className="mt-3" disabled={!!busy} onClick={() => run("import", "Read the /shop/ website now (read-only) and import/update its individual products? Bundles are not touched.")} data-testid="nc-import-btn">Import individual products from website</Button>
      </div>
      <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="nc-old">
        <h3 className="font-semibold mb-2">Old accessories</h3>
        <Row id="old-total" label="Old accessory / old web-shop products" value={plan.old_products} />
        <Row id="old-hidden" label="Hidden from Products, EPOS and product pickers" value={plan.old_hidden} />
        <Row id="old-open" label="Still used by open records (those keep working)" value={plan.old_still_used_by_open_records?.length} />
        <p className="mt-2 text-xs text-slate-500">Hiding never deletes or remaps anything. Past jobs, orders and invoices keep their original products and descriptions. Only products shown in the Products → Accessories list are hidden; the Parts and Hot Tubs lists are not touched.</p>
        <div className="mt-3 flex gap-2">
          <Button disabled={!!busy || plan.old_hidden === plan.old_products} onClick={() => run("hide-old-products", `Hide ${plan.old_products - plan.old_hidden} old accessory products from current selection? History is not changed and this can be undone.`)} data-testid="nc-hide-old-btn">Hide old accessories</Button>
          <Button variant="outline" disabled={!!busy || !plan.old_hidden} onClick={() => run("restore-old-products", `Show ${plan.old_hidden} old products again?`)} data-testid="nc-restore-old-btn">Undo</Button>
        </div>
      </div>
      <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="nc-gaps">
        <h3 className="font-semibold mb-2">Data gaps</h3>
        <Row id="img-remote" label="Images still loading from the website" value={gaps?.images_not_copied} />
        <Row id="img-none" label="Products with no image on the website" value={gaps?.no_image_on_website} />
        {ic && <p className="text-xs text-slate-500 mt-1" data-testid="nc-img-status">Image copy {ic.running ? "running" : "finished"}: {ic.done}/{ic.total} saved, {ic.failed} failed</p>}
        <Button className="mt-2" variant="outline" disabled={!!busy || ic?.running || !gaps?.images_not_copied} onClick={() => run("images/copy", `Copy ${gaps?.images_not_copied} product images into TedOps?`)} data-testid="nc-copy-images-btn">Copy images into TedOps</Button>
        <h4 className="mt-4 text-sm font-semibold">Missing selling price ({gaps?.no_price?.length ?? 0})</h4>
        <p className="text-xs text-slate-500">These can't be sold in EPOS until a price is set on the product.</p>
        <ul className="mt-1 max-h-56 overflow-auto text-sm" data-testid="nc-no-price">
          {(gaps?.no_price || []).map((p) => <li key={p.legacy_id}><Link className="text-indigo-700 hover:underline" to={`/products/${p.legacy_id}`}>#{p.legacy_id} {p.title}</Link>{p.product_type === "bundle" ? " (bundle)" : ""}</li>)}
        </ul>
      </div>
      <WebPushCard />
    </div>
  );
}

function WebPushCard() {
  const [s, setS] = useState(null);
  const [tb, setTb] = useState("");
  useEffect(() => { api.get("/new-catalogue/bundle-web-push/settings").then((r) => { setS(r.data); setTb(r.data.test_bundle || ""); }).catch(() => {}); }, []);
  const save = async (mode, test_bundle) => {
    if (mode === "all" && !window.confirm("Switch on website updates for ALL bundles? Every bundle save will immediately change the live /shop/ website.")) return;
    try { const r = await api.put("/new-catalogue/bundle-web-push/settings", { mode, test_bundle: test_bundle || null }); setS(r.data); toast.success("Website update setting saved"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
  };
  if (!s) return null;
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="nc-webpush">
      <h3 className="font-semibold mb-2">Bundle changes → website</h3>
      <p className="text-xs text-slate-500">When a bundle's products are saved in TedOps, the /shop/ website bundle is updated straight away.</p>
      <p className="mt-2 text-sm" data-testid="nc-webpush-mode">Now: <b>{s.mode === "all" ? "All bundles" : s.mode === "off" ? "Off" : s.test_bundle ? `Test bundle #${s.test_bundle} only` : "Off until a test bundle is chosen"}</b></p>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <input className="h-8 w-32 rounded border border-slate-200 px-2 text-sm" placeholder="Bundle ID" value={tb} onChange={(e) => setTb(e.target.value.trim())} data-testid="nc-webpush-test-input" />
        <Button size="sm" variant="outline" disabled={!tb} onClick={() => save("test", tb)} data-testid="nc-webpush-test-btn">Test with this bundle only</Button>
        <Button size="sm" onClick={() => save("all")} data-testid="nc-webpush-all-btn">Switch on for all bundles</Button>
        <Button size="sm" variant="ghost" onClick={() => save("off")} data-testid="nc-webpush-off-btn">Off</Button>
      </div>
    </div>
  );
}
